import { useMemo } from 'react';
import type { EntityLabel } from '@esg/esg-global-types';
import { Button, Tooltip } from '@mui/material';
import { GridToolbarContainer, GridToolbarExport, GridToolbarQuickFilter } from '@mui/x-data-grid';
import SyncIcon from '@mui/icons-material/Sync';
import { ReactComponent as CreateIcon } from '../../../assets/icons/Create.svg';
import { ReactComponent as SearchIcon } from '../../../assets/icons/Search.svg';
import { ReactComponent as ExportIcon } from '../../../assets/icons/Download.svg';
import PrimaryButton from '../button/PrimaryButton';
import { createCSVFileNameForEntity } from '../../../util/csv';

interface DataGridToolbarProps {
  entity_label: EntityLabel;
  disable_export?: boolean;
  handleCreate?: () => void;
  disable_create?: boolean;
  handleSync?: () => void;
  disable_sync?: boolean;
  is_syncing?: boolean;
}

/**
 * Reusable toolbar for datagrids allowing for customizable functionality.
 * @param {EntityLabel} entity_label Label of entity represented in datagrid for tooltips.
 * @param {boolean | undefined} disable_export Optionally disable the display of the MUI datagrid export functionality.
 * @param {void | undefined} handleCreate Optionally provide a create function for the create button.
 * @param {void} handleSync Function to trigger syncing emission factors.
 * @param {boolean} disable_sync Disables the sync button.
 * @param {boolean} is_syncing Indicates whether syncing is currently in progress.
 * @returns {JSX.Element}
 */
const DataGridToolbar = ({
  entity_label,
  disable_export,
  handleCreate,
  disable_create,
  handleSync,
  disable_sync,
  is_syncing
}: DataGridToolbarProps) => {
  const csv_file_name: string = useMemo(
    () => createCSVFileNameForEntity(entity_label.many),
    [entity_label]
  );

  return (
    <>
      <GridToolbarContainer>
        <GridToolbarQuickFilter
          slotProps={{
            input: {
              startAdornment: <SearchIcon height={15} style={{ fill: 'currentcolor' }} />
            }
          }}
          style={{ flex: 1 }}
        />
        {handleSync && (
          <Tooltip title={`Replicate latest ${entity_label.many} to company`}>
            <Button size="small" disabled={disable_sync} loading={is_syncing} onClick={handleSync}>
              <SyncIcon />
            </Button>
          </Tooltip>
        )}
        {!disable_export && (
          <Tooltip title={`Export ${entity_label.many} to device`}>
            <GridToolbarExport
              startIcon={
                <ExportIcon height={25} style={{ stroke: 'currentcolor', strokeWidth: 2 }} />
              }
              printOptions={{ disableToolbarButton: true }}
              csvOptions={{
                fileName: csv_file_name,
                delimiter: ';'
              }}
            />
          </Tooltip>
        )}
        {handleCreate && (
          <Tooltip title={`Create a New ${entity_label.one}`}>
            <PrimaryButton
              startIcon={<CreateIcon height={25} />}
              onClick={handleCreate}
              disabled={!!disable_create}
            >
              Create
            </PrimaryButton>
          </Tooltip>
        )}
      </GridToolbarContainer>
    </>
  );
};

export default DataGridToolbar;
